import React, { useEffect, useState, useRef, memo } from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    useTheme,
    CssBaseline,
    alpha,
    Grid,
    Stack,
} from '@mui/material';
import {
    School as SchoolIcon,
    Security,
    Email,
    Phone,
    LocationOn,
    Computer,
    People,
    Rocket,
    Timeline,
    Star,
    WorkspacePremium,
    SupportAgent,
    Cloud,
    Speed,
    IntegrationInstructions,
    Analytics,
    MobileFriendly,
    TrendingUp,
    EmojiEvents,
    Psychology,
    Handshake,
    Diversity3,
    Lightbulb,
    TrackChanges,
    Visibility,
    Verified,
    GpsFixed,
    AutoAwesome as SparkleIcon,
} from '@mui/icons-material';

/* ================================================================== */
/*                          STATIC DATA                                */
/* ================================================================== */

const stats = [
    { value: '1000', suffix: '+', label: 'Schools Trust Us', icon: <SchoolIcon />, color: '#6366f1' },
    { value: '50', suffix: 'K+', label: 'Active Users', icon: <People />, color: '#f59e0b' },
    { value: '98', suffix: '%', label: 'Satisfaction Rate', icon: <Star />, color: '#10b981' },
    { value: '24', suffix: '/7', label: 'Customer Support', icon: <SupportAgent />, color: '#ec4899' },
];

const values = [
    {
        icon: <Diversity3 />,
        title: 'Collaboration',
        description: 'Working together with schools to create better educational outcomes.',
        color: '#6366f1',
    },
    {
        icon: <Lightbulb />,
        title: 'Innovation',
        description: 'Continuously evolving our platform with cutting-edge technology.',
        color: '#f59e0b',
    },
    {
        icon: <Verified />,
        title: 'Integrity',
        description: 'Operating with transparency, honesty, and ethical practices.',
        color: '#10b981',
    },
    {
        icon: <Psychology />,
        title: 'Excellence',
        description: 'Delivering high-quality solutions that exceed expectations.',
        color: '#a855f7',
    },
    {
        icon: <GpsFixed />,
        title: 'Impact',
        description: 'Making a meaningful difference in education through technology.',
        color: '#ec4899',
    },
    {
        icon: <Handshake />,
        title: 'Trust',
        description: 'Building lasting relationships through reliability and support.',
        color: '#06b6d4',
    },
];

const features = [
    {
        icon: <Cloud />,
        title: 'Cloud-Based',
        description: 'Access your school data anytime, anywhere with our secure cloud platform.',
        color: '#0ea5e9',
    },
    {
        icon: <Security />,
        title: 'Enterprise Security',
        description: 'Bank-grade encryption and security protocols protect your sensitive data.',
        color: '#6366f1',
    },
    {
        icon: <Speed />,
        title: 'High Performance',
        description: 'Lightning-fast response times with optimized infrastructure.',
        color: '#f59e0b',
    },
    {
        icon: <IntegrationInstructions />,
        title: 'Seamless Integration',
        description: 'Connect with payment gateways, SMS services, and more.',
        color: '#10b981',
    },
    {
        icon: <Analytics />,
        title: 'Advanced Analytics',
        description: 'Powerful reporting and insights to drive better decisions.',
        color: '#a855f7',
    },
    {
        icon: <MobileFriendly />,
        title: 'Mobile Ready',
        description: 'Responsive design works on desktops, tablets, and smartphones.',
        color: '#ec4899',
    },
];

const milestones = [
    {
        year: '2021',
        title: 'Founded',
        description: 'JMKINFO was established with a vision to revolutionize school management.',
        icon: <Rocket />,
    },
    {
        year: '2022',
        title: 'First Launch',
        description: 'Launched the initial version of the School Management System.',
        icon: <Computer />,
    },
    {
        year: '2023',
        title: '100+ Schools',
        description: 'Reached 100+ schools across India using our platform.',
        icon: <SchoolIcon />,
    },
    {
        year: '2023',
        title: 'Advanced Features',
        description: 'Introduced AI-powered analytics and automated reporting.',
        icon: <TrendingUp />,
    },
    {
        year: '2024',
        title: '500+ Schools',
        description: 'Surpassed 500 schools with enhanced cloud infrastructure.',
        icon: <Cloud />,
    },
    {
        year: '2025',
        title: 'Global Expansion',
        description: 'Expanded services to national educational institutions.',
        icon: <Timeline />,
    },
    {
        year: '2024',
        title: 'Innovation Award',
        description: 'Recognized for excellence in educational technology innovation.',
        icon: <EmojiEvents />,
    },
    {
        year: '2026',
        title: '1000+ Schools',
        description: 'Proudly serving 1000+ schools with comprehensive management solutions.',
        icon: <WorkspacePremium />,
    },
];

/* ================================================================== */
/*                       SECURITY GUARDS                               */
/* ================================================================== */

const disableRightClick = (e) => {
    e.preventDefault();
    return false;
};

const disableKeyboardShortcuts = (e) => {
    if (
        (e.ctrlKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key)) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['i', 'I', 'j', 'J'].includes(e.key)) ||
        (e.metaKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key))
    ) {
        e.preventDefault();
        return false;
    }
};

const disableTextSelection = (e) => {
    const target = e.target;
    if (target && target.closest && target.closest('.no-select')) {
        e.preventDefault();
        return false;
    }
};

/* ================================================================== */
/*                       ANIMATED COUNTER                              */
/* ================================================================== */

const AnimatedCounter = memo(function AnimatedCounter({ value, duration = 1500, suffix = '' }) {
    const [display, setDisplay] = useState(0);
    const ref = useRef(null);
    const startedRef = useRef(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !startedRef.current) {
                    startedRef.current = true;
                    const start = performance.now();
                    const numeric = parseFloat(value);
                    const isFloat = value.toString().includes('.');

                    const tick = (now) => {
                        const progress = Math.min((now - start) / duration, 1);
                        const eased = 1 - Math.pow(1 - progress, 4);
                        const current = numeric * eased;
                        setDisplay(isFloat ? current.toFixed(1) : Math.floor(current));
                        if (progress < 1) requestAnimationFrame(tick);
                        else setDisplay(isFloat ? numeric.toFixed(1) : numeric);
                    };
                    requestAnimationFrame(tick);
                    observer.disconnect();
                }
            },
            { threshold: 0.3 }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [value, duration]);

    return (
        <Box component="span" ref={ref}>
            {display}
            {suffix}
        </Box>
    );
});

/* ================================================================== */
/*                       SECTION TITLE                                 */
/*    (Exactly matches the SectionTitle in SchoolDashboard)            */
/* ================================================================== */

const SectionTitle = memo(function SectionTitle({ title, subtitle, badge, icon }) {
    return (
        <Box sx={{ mb: 4, mt: 9 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Typography
                    sx={{
                        fontSize: '0.68rem',
                        fontWeight: 900,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: 'primary.main',
                    }}
                >
                    {badge}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider', position: 'relative' }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: -1,
                            width: 40,
                            height: 3,
                            borderRadius: 1.5,
                            bgcolor: 'primary.main',
                        }}
                    />
                </Box>
            </Stack>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 1, md: 2 }}
                alignItems={{ md: 'flex-end' }}
            >
                <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: '1.75rem', md: '2.5rem' },
                        letterSpacing: '-0.04em',
                        lineHeight: 1.1,
                        maxWidth: 620,
                    }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        sx={{
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            color: 'text.secondary',
                            lineHeight: 1.5,
                            maxWidth: 480,
                            fontWeight: 400,
                            pb: 0.5,
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
});

/* ================================================================== */
/*                       INFO CARD                                     */
/*    (Matches FeatureCard hover behavior in SchoolDashboard)          */
/* ================================================================== */

const InfoCard = memo(function InfoCard({ icon, title, description, color = '#6366f1' }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.6),
                bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
                backdropFilter: 'blur(20px)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: alpha(color, 0.35),
                    boxShadow: `0 24px 48px -16px ${alpha(color, 0.25)}`,
                    '& .info-icon': {
                        bgcolor: alpha(color, 0.15),
                        color: color,
                        transform: 'scale(1.05)',
                    },
                    '& .info-title': { color },
                },
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box
                    className="info-icon"
                    sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isDark ? alpha('#334155', 0.4) : alpha('#f1f5f9', 1),
                        color: 'text.secondary',
                        mb: 2,
                        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& svg': { fontSize: 22 },
                    }}
                >
                    {icon}
                </Box>
                <Typography
                    className="info-title"
                    variant="h6"
                    component="h3"
                    sx={{
                        fontWeight: 800,
                        fontSize: '1rem',
                        letterSpacing: '-0.015em',
                        mb: 1,
                        transition: 'color 0.3s',
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '0.82rem',
                        color: 'text.secondary',
                        lineHeight: 1.55,
                        fontWeight: 500,
                    }}
                >
                    {description}
                </Typography>
            </CardContent>
        </Card>
    );
});

/* ================================================================== */
/*                          MAIN COMPONENT                             */
/* ================================================================== */

const AboutUs = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    useEffect(() => {
        document.addEventListener('contextmenu', disableRightClick);
        document.addEventListener('keydown', disableKeyboardShortcuts);
        document.addEventListener('selectstart', disableTextSelection);
        document.addEventListener('dragstart', disableTextSelection);

        const preventCopy = (e) => e.preventDefault();
        document.addEventListener('copy', preventCopy);
        document.addEventListener('cut', preventCopy);

        return () => {
            document.removeEventListener('contextmenu', disableRightClick);
            document.removeEventListener('keydown', disableKeyboardShortcuts);
            document.removeEventListener('selectstart', disableTextSelection);
            document.removeEventListener('dragstart', disableTextSelection);
            document.removeEventListener('copy', preventCopy);
            document.removeEventListener('cut', preventCopy);
        };
    }, []);

    return (
        <CssBaseline>
            <Box
                className="no-select"
                sx={{
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    minHeight: '100vh',
                }}
            >
                <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
                    {/* ============ HERO BANNER ============ */}
                    <Box
                        sx={{
                            position: 'relative',
                            borderRadius: 5,
                            p: { xs: 3, md: 6 },
                            overflow: 'hidden',
                            background: isDark
                                && 'linear-gradient(135deg, #00365600 0%, #082f4945 50%, #1e1b4b 100%)',
                            // : 'linear-gradient(135deg, #082f49 0%, #000000 50%, #00045ffc 100%)',
                            // color: '#fff',
                            boxShadow: isDark
                                ? '0 40px 80px -30px rgba(99,102,241,0.4)'
                                : '0 40px 80px -30px rgba(3,105,161,0.5)',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'radial-gradient(circle at 20% 0%, rgba(245,158,11,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(99,102,241,0.35) 0%, transparent 50%)',
                                pointerEvents: 'none',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                backgroundImage:
                                    'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
                                backgroundSize: '50px 50px',
                                maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
                                WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
                                pointerEvents: 'none',
                            },
                        }}
                    >
                        {/* Floating orb */}
                        <Box
                            sx={{
                                position: 'absolute',
                                width: 300,
                                height: 300,
                                borderRadius: '50%',
                                top: -100,
                                right: -80,
                                background: 'radial-gradient(circle, rgba(245,158,11,0.4) 0%, transparent 70%)',
                                filter: 'blur(40px)',
                                pointerEvents: 'none',
                            }}
                        />

                        <Box sx={{ maxWidth: 780, position: 'relative', zIndex: 1 }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    px: 1.75,
                                    py: 0.75,
                                    mb: 3,
                                    borderRadius: 4,
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        bgcolor: '#f59e0b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <SparkleIcon sx={{ fontSize: 13, color: '#0f172a' }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.02em' }}>
                                    About JMKINFO · Est. 2021
                                </Typography>
                            </Box>

                            <Typography
                                component="h1"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                                    lineHeight: 1.05,
                                    letterSpacing: '-0.04em',
                                    mb: 2.5,
                                }}
                            >
                                Transforming education
                                <br />
                                <Box
                                    component="span"
                                    sx={{
                                        marginLeft: '2em',
                                        background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 50%, #ec4899 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    through technology.
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    opacity: 0.85,
                                    mb: 4,
                                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                                    lineHeight: 1.7,
                                    maxWidth: 620,
                                }}
                            >
                                JMKINFO is dedicated to empowering educational institutions with
                                innovative, cloud-based management solutions that streamline
                                operations and enhance learning outcomes.
                            </Typography>

                            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                                {[
                                    { icon: <Security sx={{ fontSize: 16 }} />, label: 'Enterprise Security' },
                                    { icon: <Cloud sx={{ fontSize: 16 }} />, label: 'Cloud-First' },
                                    { icon: <Verified sx={{ fontSize: 16 }} />, label: 'ISO Certified' },
                                ].map((chip, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 1.75,
                                            py: 0.85,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(255,255,255,0.08)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255,255,255,0.15)',
                                            transition: 'all 0.3s',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,255,255,0.14)',
                                                transform: 'translateY(-2px)',
                                            },
                                            '& svg': { color: '#fbbf24' },
                                        }}
                                    >
                                        {chip.icon}
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                                            {chip.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Box>

                    {/* ============ STATS ============ */}
                    <Grid container spacing={2.5} sx={{ mt: 4 }}>
                        {stats.map((stat, idx) => (
                            <Grid item xs={6} md={3} key={idx}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.6),
                                        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            borderColor: alpha(stat.color, 0.4),
                                            boxShadow: `0 24px 48px -16px ${alpha(stat.color, 0.3)}`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2.5,
                                            bgcolor: alpha(stat.color, 0.12),
                                            color: stat.color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2,
                                            '& svg': { fontSize: 24 },
                                        }}
                                    >
                                        {stat.icon}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                                            lineHeight: 1,
                                            letterSpacing: '-0.03em',
                                            color: stat.color,
                                        }}
                                    >
                                        <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '0.7rem',
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            mt: 1,
                                        }}
                                    >
                                        {stat.label}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* ============ OUR STORY ============ */}
                    <SectionTitle
                        badge="Our Story"
                        title="From a small idea to 1000+ schools."
                        subtitle="Founded in Nagpur, serving institutions across India and beyond."
                    />

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 3.5,
                                    borderRadius: 3,
                                    bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.divider, 0.6),
                                    height: '100%',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.85,
                                        mb: 2.5,
                                        fontSize: '0.92rem',
                                    }}
                                >
                                    Founded in{' '}
                                    <Box component="strong" sx={{ color: 'primary.main' }}>
                                        2021
                                    </Box>
                                    , JMKINFO began with a simple yet powerful vision: to create a
                                    comprehensive, user-friendly school management system that
                                    would simplify administrative tasks and enhance the educational
                                    experience.
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.85,
                                        fontSize: '0.92rem',
                                    }}
                                >
                                    What started as a small project in Nagpur has grown into a
                                    trusted platform serving over{' '}
                                    <Box component="strong" sx={{ color: 'primary.main' }}>
                                        1000+ schools
                                    </Box>{' '}
                                    across India and beyond. Our journey has been driven by a
                                    passion for education and a commitment to leveraging
                                    technology for positive change.
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>
                                {milestones.slice(0, 4).map((milestone, idx) => (
                                    <Grid item xs={6} key={idx}>
                                        <Card
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 3,
                                                bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
                                                backdropFilter: 'blur(20px)',
                                                border: '1px solid',
                                                borderColor: alpha(theme.palette.divider, 0.6),
                                                textAlign: 'center',
                                                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    borderColor: alpha(theme.palette.primary.main, 0.35),
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: `0 20px 40px -16px ${alpha(
                                                        theme.palette.primary.main,
                                                        0.25
                                                    )}`,
                                                    '& .milestone-icon': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                                                        color: theme.palette.primary.main,
                                                        transform: 'scale(1.05)',
                                                    },
                                                },
                                            }}
                                        >
                                            <Box
                                                className="milestone-icon"
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: 2.5,
                                                    bgcolor: isDark
                                                        ? alpha('#334155', 0.4)
                                                        : alpha('#f1f5f9', 1),
                                                    color: 'text.secondary',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mx: 'auto',
                                                    mb: 1.5,
                                                    transition: 'all 0.35s',
                                                    '& svg': { fontSize: 22 },
                                                }}
                                            >
                                                {milestone.icon}
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.68rem',
                                                    fontWeight: 900,
                                                    letterSpacing: '0.1em',
                                                    color: 'primary.main',
                                                    mb: 0.5,
                                                }}
                                            >
                                                {milestone.year}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>
                                                {milestone.title}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* ============ MISSION & VISION ============ */}
                    <SectionTitle
                        badge="Purpose"
                        title="Mission & vision."
                        subtitle="What drives us forward, every single day."
                    />

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: 3,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                    height: '100%',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 4,
                                        background:
                                            'linear-gradient(90deg, #6366f1 0%, #818cf8 50%, transparent)',
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={2} mb={2.5}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2.5,
                                            bgcolor: alpha('#6366f1', 0.12),
                                            color: '#6366f1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <TrackChanges sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: '0.65rem',
                                                fontWeight: 900,
                                                letterSpacing: '0.15em',
                                                color: 'text.secondary',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            Our Mission
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.15rem' }}>
                                            Empower every school
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.8,
                                        fontSize: '0.92rem',
                                    }}
                                >
                                    To empower educational institutions with intuitive, secure, and
                                    comprehensive technology solutions that streamline
                                    administration, improve communication, and enable data-driven
                                    decision-making.
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: 3,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid',
                                    borderColor: alpha('#f59e0b', 0.2),
                                    height: '100%',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 4,
                                        background:
                                            'linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, transparent)',
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={2} mb={2.5}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2.5,
                                            bgcolor: alpha('#f59e0b', 0.12),
                                            color: '#f59e0b',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: '0.65rem',
                                                fontWeight: 900,
                                                letterSpacing: '0.15em',
                                                color: 'text.secondary',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            Our Vision
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.15rem' }}>
                                            The most trusted platform
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.8,
                                        fontSize: '0.92rem',
                                    }}
                                >
                                    To be the most trusted and innovative educational management
                                    platform globally, transforming how schools operate and
                                    enabling better outcomes for students, teachers, and
                                    administrators.
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* ============ VALUES ============ */}
                    <SectionTitle
                        badge="Values · 6 principles"
                        title="What we stand for."
                        subtitle="The principles that guide every decision we make."
                    />

                    <Grid container spacing={2.5}>
                        {values.map((value, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                <InfoCard {...value} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* ============ FEATURES ============ */}
                    <SectionTitle
                        badge="Why Us · 6 pillars"
                        title="Why choose JMKINFO?"
                        subtitle="Built for schools, by people who understand schools."
                    />

                    <Grid container spacing={2.5}>
                        {features.map((feature, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                <InfoCard {...feature} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* ============ JOURNEY TIMELINE ============ */}
                    <SectionTitle
                        badge="Journey · 2021 → 2025"
                        title="Our milestones."
                        subtitle="Every step took us closer to transforming education."
                    />

                    <Box
                        sx={{
                            p: { xs: 2.5, md: 4 },
                            borderRadius: 3,
                            bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
                            backdropFilter: 'blur(20px)',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.6),
                        }}
                    >
                        <Grid container spacing={3}>
                            {milestones.map((milestone, idx) => (
                                <Grid item xs={12} sm={6} md={3} key={idx}>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            '&:hover .timeline-icon': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                                color: theme.palette.primary.main,
                                                transform: 'scale(1.1)',
                                                boxShadow: `0 12px 24px -8px ${alpha(
                                                    theme.palette.primary.main,
                                                    0.5
                                                )}`,
                                            },
                                        }}
                                    >
                                        <Box
                                            className="timeline-icon"
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: '50%',
                                                bgcolor: isDark
                                                    ? alpha('#334155', 0.4)
                                                    : alpha('#f1f5f9', 1),
                                                color: 'text.secondary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 1.5,
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '& svg': { fontSize: 26 },
                                            }}
                                        >
                                            {milestone.icon}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: '0.7rem',
                                                fontWeight: 900,
                                                letterSpacing: '0.1em',
                                                color: 'primary.main',
                                            }}
                                        >
                                            {milestone.year}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '0.9rem',
                                                mt: 0.5,
                                                mb: 0.5,
                                            }}
                                        >
                                            {milestone.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {milestone.description}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* ============ CONTACT CTA ============ */}
                    <Box
                        sx={{
                            mt: 6,
                            borderRadius: 4,
                            border: isDark ? '1px solid #3e3e3e' : "1px solid #cfcfcf",
                            p: { xs: 2, md: 5 },
                            position: 'relative',
                            overflow: 'hidden',
                            background: isDark
                                && 'linear-gradient(135deg, #0134523e 0%, #1e1b4b 100%)'
                            // : 'linear-gradient(135deg, #082f49 0%, #4f46e5 100%)',
                            // color: '#fff',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                width: 300,
                                height: 300,
                                borderRadius: '50%',
                                top: -100,
                                right: -80,
                                background:
                                    'radial-gradient(circle, rgba(245,158,11,0.35) 0%, transparent 70%)',
                                filter: 'blur(40px)',
                            }}
                        />
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                            sx={{ position: 'relative', zIndex: 1 }}
                        >
                            <Grid item xs={12} md={7}>
                                <Typography
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '1.5rem', md: '1.9rem' },
                                        letterSpacing: '-0.03em',
                                        mb: 1.5,
                                        lineHeight: 1.15,
                                    }}
                                >
                                    Join us in transforming education.
                                </Typography>
                                <Typography
                                    sx={{
                                        opacity: 0.85,
                                        fontSize: '0.92rem',
                                        lineHeight: 1.65,
                                        maxWidth: 520,
                                    }}
                                >
                                    Whether you're a school looking for better management solutions
                                    or an educator passionate about technology in education, we'd
                                    love to hear from you.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Stack spacing={1.5}>
                                    {[
                                        {
                                            icon: <LocationOn sx={{ fontSize: 18 }} />,
                                            text: 'Nagpur, Maharashtra, India',
                                        },
                                        {
                                            icon: <Email sx={{ fontSize: 18 }} />,
                                            text: 'jmk.infotech2021@gmail.com',
                                        },
                                        {
                                            icon: <Phone sx={{ fontSize: 18 }} />,
                                            text: '+91 97306 76527',
                                        },
                                    ].map((item, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.25,
                                                borderRadius: 2.5,
                                                bgcolor: 'rgba(255,255,255,0.08)',
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(255,255,255,0.12)',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    bgcolor: 'rgba(255,255,255,0.14)',
                                                    transform: 'translateX(4px)',
                                                },
                                            }}
                                        >
                                            <Box sx={{ color: '#fbbf24', display: 'flex' }}>
                                                {item.icon}
                                            </Box>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                                {item.text}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* ============ FOOTER ============ */}

                </Container>
            </Box>
        </CssBaseline>
    );
};

export default AboutUs;